// Fechas, siempre en hora de Buenos Aires.
//
// Por que existe este archivo: el Worker corre en UTC. Un partido del viernes
// a las 23:30 en Argentina es SABADO en UTC — y si eso pasa un 31, al jugador
// le cambia el mes y le puede costar el campeonato. Asi que en toda la app la
// fecha de un partido es un 'YYYY-MM-DD' ya resuelto en zona argentina, y
// nunca un timestamp interpretado al vuelo.

export const ZONA = 'America/Argentina/Buenos_Aires';

/** Antigüedad máxima para cargar un partido, compartida por pantalla y API. */
export const DIAS_HACIA_ATRAS = 30;

/** Hoy en Buenos Aires, como 'YYYY-MM-DD'. */
export function hoy(ahora: Date = new Date()): string {
  return aFechaLocal(ahora);
}

/** Convierte un instante a la fecha calendaria que era en Buenos Aires. */
export function aFechaLocal(instante: Date): string {
  // 'en-CA' da justo el formato YYYY-MM-DD.
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: ZONA,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(instante);
}

/** El mes ('YYYY-MM') al que pertenece una fecha. */
export function mesDe(fecha: string): string {
  return fecha.slice(0, 7);
}

const FORMA = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Valida que sea una fecha real con el formato esperado.
 * Rechaza cosas como '2026-02-30', que pasan el regex pero no existen.
 */
export function esFechaValida(fecha: string): boolean {
  if (!FORMA.test(fecha)) return false;
  const [a, m, d] = fecha.split('-').map(Number) as [number, number, number];
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const prueba = new Date(Date.UTC(a, m - 1, d));
  return (
    prueba.getUTCFullYear() === a &&
    prueba.getUTCMonth() === m - 1 &&
    prueba.getUTCDate() === d
  );
}

/** Dias enteros entre dos fechas calendarias (b - a). Puede dar negativo. */
export function diasEntre(a: string, b: string): number {
  const ms = Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`);
  return Math.round(ms / 86_400_000);
}

/** Suma (o resta) meses a un 'YYYY-MM'. */
export function sumarMeses(mes: string, cuantos: number): string {
  const [a, m] = mes.split('-').map(Number) as [number, number];
  const total = a * 12 + (m - 1) + cuantos;
  const anio = Math.floor(total / 12);
  const nuevoMes = (total % 12) + 1;
  return `${String(anio).padStart(4, '0')}-${String(nuevoMes).padStart(2, '0')}`;
}

/** Primer y ultimo dia de un mes 'YYYY-MM'. */
export function limitesDelMes(mes: string): { desde: string; hasta: string } {
  const [a, m] = mes.split('-').map(Number) as [number, number];
  const ultimo = new Date(Date.UTC(a, m, 0)).getUTCDate();
  return { desde: `${mes}-01`, hasta: `${mes}-${String(ultimo).padStart(2, '0')}` };
}

/**
 * El mes cierra el 1 del mes siguiente a esta hora de Buenos Aires.
 *
 * Decidido por Marcos el 2026-09-24 (antes eran 7 dias despues de terminado).
 * Lo normal es cargar el partido la misma noche; las horas hasta las 6 son el
 * margen para los partidos del ultimo dia del mes, cuando ese dia se juega. Al
 * cerrar, los pendientes del mes se confirman solos (ver cron.ts) y desde ahi
 * el mes solo lo toca un admin. El cron corre cada hora en punto, asi que
 * cierra a las 6 justas.
 */
export const HORA_DE_CIERRE = 6;

/**
 * Cuantos dias espera un pendiente antes de darse por bueno solo.
 *
 * Vive aca y no en el cron porque la web tambien lo muestra: si el numero del
 * cartel y el del cron se separaran, la app le mentiria al grupo.
 */
export const DIAS_PARA_AUTOCONFIRMAR = 1;

/** Para los carteles: '1 día', '3 días'. */
export function plazoAutoconfirmar(): string {
  return DIAS_PARA_AUTOCONFIRMAR === 1 ? '1 día' : `${DIAS_PARA_AUTOCONFIRMAR} días`;
}

/**
 * El instante (milisegundos) en que cierra un mes: el 1 del mes siguiente a
 * las HORA_DE_CIERRE de Buenos Aires. Argentina no tiene horario de verano, asi
 * que el -03:00 es fijo.
 */
export function cierreDelMes(mes: string): number {
  const siguiente = sumarMeses(mes, 1);
  return Date.parse(`${siguiente}-01T${String(HORA_DE_CIERRE).padStart(2, '0')}:00:00-03:00`);
}

export function sePuedeCerrar(mes: string, ahora: number = Date.now()): boolean {
  return ahora >= cierreDelMes(mes);
}

/** Para mostrar: '2026-09' -> 'septiembre 2026'. */
const MESES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

export function mesEnPalabras(mes: string): string {
  const [a, m] = mes.split('-').map(Number) as [number, number];
  return `${MESES[m - 1]} ${a}`;
}

/**
 * Un instante guardado por la base (segundos unix) como se lee en Buenos Aires:
 * '23/9 21:14'. Para el historial de un partido, donde la hora es el dato: un
 * partido cargado a las 4 de la mañana de un día sin pádel llama la atención.
 */
export function fechaHora(segundos: number): { fecha: string; dia: string; hora: string } {
  const instante = new Date(segundos * 1000);
  const partes = new Intl.DateTimeFormat('es-AR', {
    timeZone: ZONA,
    weekday: 'long',
    day: 'numeric',
    month: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instante);
  const de = (t: string) => partes.find((p) => p.type === t)?.value ?? '';
  return {
    fecha: aFechaLocal(instante),
    dia: `${de('weekday')} ${de('day')}/${de('month')}`,
    hora: `${de('hour')}:${de('minute')}`,
  };
}
